import { ChevronDown, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useUserGroups } from '@/hooks/useSigners'

interface UserGroupMultiSelectProps {
  id: string
  label: string
  helpText: string
  value: string[]
  onChange: (next: string[]) => void
}

/**
 * Pick user groups by id (z8nrz7dt45) — a signature field's allowed signers,
 * or a form's allowed completers. Empty means unrestricted.
 *
 * Groups are managed in one place (Configure → User Groups) and only
 * REFERENCED here, so who is in "QA Review" can change without re-editing any
 * form. An id with no group behind it (deleted since) stays visible and
 * removable instead of silently vanishing — the server refuses to save one.
 */
export function UserGroupMultiSelect({ id, label, helpText, value, onChange }: UserGroupMultiSelectProps) {
  const groups = useUserGroups()
  const byId = new Map((groups.data ?? []).map(g => [g.id, g]))

  const toggle = (groupId: string, on: boolean) =>
    onChange(on ? [...value, groupId] : value.filter(v => v !== groupId))

  return (
    <div className='space-y-1.5'>
      <Label htmlFor={id} className='text-xs'>
        {label}
      </Label>
      {value.length > 0 && (
        <div className='flex flex-wrap gap-1'>
          {value.map(gid => (
            <span
              key={gid}
              className='inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary'
            >
              {byId.get(gid)?.name ?? (groups.isLoading ? '…' : 'Unknown group')}
              <button
                type='button'
                aria-label={`Remove ${byId.get(gid)?.name ?? 'group'}`}
                onClick={() => toggle(gid, false)}
                className='rounded-full hover:bg-primary/20'
              >
                <X className='h-3 w-3' />
              </button>
            </span>
          ))}
        </div>
      )}
      <Popover>
        <PopoverTrigger asChild>
          <Button id={id} type='button' variant='outline' size='sm' className='h-8 w-full justify-between text-sm'>
            {value.length === 0 ? 'Anyone (no restriction)' : `${value.length} group${value.length === 1 ? '' : 's'}`}
            <ChevronDown className='h-3.5 w-3.5 opacity-60' />
          </Button>
        </PopoverTrigger>
        <PopoverContent align='start' className='w-64 p-2'>
          {groups.isLoading && (
            <p className='flex items-center gap-2 p-1 text-xs text-muted-foreground'>
              <Loader2 className='h-3.5 w-3.5 animate-spin' /> Loading groups…
            </p>
          )}
          {groups.isError && (
            <p className='p-1 text-xs text-destructive'>Could not load email groups.</p>
          )}
          {groups.data?.length === 0 && (
            <p className='p-1 text-xs text-muted-foreground'>
              No email groups yet. An administrator can create them under Configure → Email Groups.
            </p>
          )}
          <div className='max-h-60 space-y-0.5 overflow-y-auto'>
            {groups.data?.map(g => (
              <label
                key={g.id}
                className='flex cursor-pointer items-center gap-2 rounded-sm px-1 py-1 text-sm hover:bg-accent'
              >
                <Checkbox
                  checked={value.includes(g.id)}
                  onCheckedChange={c => toggle(g.id, c === true)}
                />
                <span className='flex-1'>{g.name}</span>
                <span className='text-[11px] text-muted-foreground'>{g.members.length}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <p className='text-[11px] text-muted-foreground'>{helpText}</p>
    </div>
  )
}
